import { abortReason, throwIfAborted } from '../abortSignal';
import {
  beginQueueRecordPost,
  blockInterruptedQueuePost,
  claimQueueRecord,
  deleteOwnedQueueRecord,
  getQueueRecord,
  OWNERSHIP_DURATION_MS,
  releaseQueueOwnership,
  renewQueueOwnership,
  updateOwnedQueueRecord,
} from './storage';

import { acknowledgeSubmission, beginOnlineSubmissionPost, claimOnlineSubmission } from './sessionStorage';

const RENEW_INTERVAL_MS = OWNERSHIP_DURATION_MS / 3;

async function runWithLease(id, operation, signal, claimed) {
  throwIfAborted(signal);
  const record = claimed || (await claimQueueRecord(id));
  if (!record) return { acquired: false };

  const onlineSnapshot = claimed ? structuredClone(claimed) : null;

  // Keep authority separate from the mutable snapshot handed to the sender.
  const attempt = {
    id: record.id,
    apiUrl: record.apiUrl,
    submissionId: record.submissionId,
    revision: record.revision,
    kind: record.kind,
    feedbackId: record.feedbackId,
    version: record.version,
    ownership: { ...record.ownership },
  };

  const controller = new AbortController();
  const abort = () => controller.abort(abortReason(signal));
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) abort();

  let renewalTimer;
  let expiryTimer;
  let renewal;
  let stopped = false;
  let leaseExpiresAt = record.ownership.expiresAt;
  let value;
  let failure;
  let failed = false;
  let releaseError = null;

  const loseOwnership = (error = new DOMException('Queue ownership expired', 'AbortError')) => {
    controller.abort(error);
  };

  const armExpiry = (expiresAt) => {
    leaseExpiresAt = expiresAt;
    clearTimeout(expiryTimer);
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) loseOwnership();
    else expiryTimer = setTimeout(loseOwnership, remaining);
  };

  const scheduleRenewal = () => {
    renewalTimer = setTimeout(() => {
      renewal = renewQueueOwnership(attempt)
        .then((expiresAt) => {
          if (!expiresAt) loseOwnership();
          else if (!stopped && !controller.signal.aborted) armExpiry(expiresAt);
        })
        .catch(loseOwnership)
        .then(() => {
          if (!stopped && !controller.signal.aborted) scheduleRenewal();
        });
    }, RENEW_INTERVAL_MS);
  };

  try {
    armExpiry(record.ownership.expiresAt);
    throwIfAborted(controller.signal);
    scheduleRenewal();
    value = await operation(record, {
      signal: controller.signal,
      beginPost: () => {
        throwIfAborted(controller.signal);

        return onlineSnapshot ? beginOnlineSubmissionPost(onlineSnapshot) : beginQueueRecordPost(attempt);
      },
      update: (changes) => {
        throwIfAborted(controller.signal);
        return updateOwnedQueueRecord(attempt, changes);
      },
      acknowledge: (body) => {
        throwIfAborted(controller.signal);
        return acknowledgeSubmission(attempt, body);
      },
      remove: () => {
        throwIfAborted(controller.signal);
        return deleteOwnedQueueRecord(attempt);
      },
    });
    // On resume a response can settle before overdue timer callbacks run.
    if (Date.now() >= leaseExpiresAt) loseOwnership();
    throwIfAborted(controller.signal);
  } catch (error) {
    failed = true;
    failure = error;
  } finally {
    stopped = true;
    clearTimeout(renewalTimer);
    clearTimeout(expiryTimer);
    signal?.removeEventListener('abort', abort);
    // Finish any in-flight renewal before releasing the session.
    await renewal;
    try {
      const released = await releaseQueueOwnership(attempt);
      if (!released) loseOwnership();
    } catch (error) {
      // A cleanup failure must not turn an accepted POST into a send failure.
      // Keep it separate so the caller can report it without retrying the POST.
      releaseError = error;
    }
  }

  if (failed) throw failure;
  throwIfAborted(controller.signal);
  return { acquired: true, value, releaseError };
}

/**
 * Shared entry point for processing durable offline records.
 * operation must honor signal and settle only after its work has stopped; the
 * session remains held until then. Finish captcha/preparation before calling
 * beginPost and send only if it resolves true. Once committed, that revision
 * cannot be retried, even after cancellation, expiry, or a lost response.
 * Apply send results through update and remove; they check the original owner
 * and revision atomically.
 * remove is for HTTP 200 only; storage errors reject, obsolete results are no-ops.
 */
export async function withQueueRecordOwnership(id, operation, { signal } = {}) {
  throwIfAborted(signal);
  const record = await getQueueRecord(id);
  if (!record) return { acquired: false };

  return withSessionLock(record, () => runWithLease(id, operation, signal), { acquired: false });
}

/** Runs operation under the session's Web Lock when available, or returns busy. */
async function withSessionLock(record, operation, busy) {
  let enteredLock = false;
  try {
    const locks = globalThis.navigator?.locks;
    if (typeof locks?.request === 'function') {
      const name = `pisano-feedback-queue:${JSON.stringify([record.apiUrl, record.submissionId])}`;
      return await locks.request(name, { mode: 'exclusive', ifAvailable: true }, (lock) => {
        enteredLock = true;
        return lock ? operation() : busy;
      });
    }
  } catch (error) {
    // Fall back only when Web Locks itself is unavailable, never when sending
    // failed or a lock was busy. Re-running an operation could duplicate a POST.
    if (enteredLock || !['SecurityError', 'NotSupportedError'].includes(error.name)) throw error;
  }
  return operation();
}

/**
 * Records a started POST left in `sending` by a closed page or lost lease.
 * A sender that still holds the session lock or lease is left alone.
 */
export function blockInterruptedPost(record, lastError) {
  return withSessionLock(record, () => blockInterruptedQueuePost(record.id, lastError), undefined);
}

/** Wait for the previous offline attempt without creating an online queue record. */
export async function withExistingQueueSession(input, operation, { signal } = {}) {
  while (true) {
    throwIfAborted(signal);
    const claimed = await claimOnlineSubmission(input);

    if (claimed?.status === 'skipped') return claimed;
    if (claimed) {
      let accepted;

      try {
        const owned = await runWithLease(
          null,
          async (record, context) => {
            accepted = await operation(record, context);

            return accepted;
          },
          signal,
          claimed,
        );
        if (owned.releaseError && accepted.status === 'submitted') {
          accepted.cleanupErrors.push({ operation: 'release', name: owned.releaseError.name });
        }

        return accepted;
      } catch (error) {
        if (accepted?.status !== 'submitted') throw error;

        accepted.cleanupErrors.push({ operation: 'ownership', name: error.name });

        return accepted;
      }
    }

    await new Promise((resolve, reject) => {
      const done = () => {
        signal?.removeEventListener('abort', abort);
        resolve();
      };
      const timer = setTimeout(done, 100);
      const abort = () => {
        clearTimeout(timer);
        signal.removeEventListener('abort', abort);
        reject(abortReason(signal));
      };
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) abort();
    });
  }
}
